import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  Chart,
  ChartData,
  ChartOptions,
  ArcElement,
  DoughnutController,
} from 'chart.js';

Chart.register(ArcElement, DoughnutController);

@Component({
  selector: 'app-rsst-donut',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="donut-wrap">
      <div class="donut-chart">
        <canvas #donutCanvas></canvas>
        <div class="donut-center">{{ centerValue() }}</div>
      </div>
      <div class="donut-legend">
        @for (item of legendItems(); track $index) {
          <div class="legend-item">
            <span class="legend-dot" [style.background-color]="item.color"></span>
            <span class="legend-label">{{ item.label }}</span>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .donut-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-sm);
      flex: 1;
    }
    .donut-chart {
      position: relative;
      width: 112px;
      height: 112px;
    }
    .donut-chart canvas {
      width: 100% !important;
      height: 100% !important;
    }
    .donut-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 20px;
      font-weight: 700;
      color: var(--gokuri-text-primary);
    }
    .donut-legend {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: var(--space-xs) var(--space-md);
    }
    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .legend-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .legend-label {
      font-size: var(--font-size-label-sm);
      color: var(--gokuri-text-secondary);
    }
  `],
})
export class RsstDonutComponent implements AfterViewInit, OnChanges {
  readonly pieChartData = input<number[]>([]);
  readonly pieChartColor = input<string[]>([]);
  readonly centerValue = input<string | number>('');
  readonly legendItems = input<{ label: string; color: string }[]>([]);

  @ViewChild('donutCanvas') set loadChart(canvas: ElementRef<HTMLCanvasElement> | undefined) {
    if (!canvas) return;
    this.chart = new Chart(canvas.nativeElement.getContext('2d')!, {
      type: 'doughnut',
      data: this.buildChartData(),
      options: this.chartOptions,
    });
  }

  private chart: Chart | null = null;

  private readonly chartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: '70%',
    plugins: {
      tooltip: { enabled: false },
      legend: { display: false },
    },
  };

  ngAfterViewInit(): void {
    this.updateChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pieChartData'] || changes['pieChartColor']) {
      this.updateChart();
    }
  }

  private buildChartData(): ChartData {
    return {
      datasets: [{
        data: this.pieChartData(),
        backgroundColor: this.pieChartColor(),
        borderWidth: 1,
      }],
    };
  }

  private updateChart(): void {
    if (!this.chart) return;
    this.chart.data = this.buildChartData();
    this.chart.update();
  }
}
